import React, { Component } from 'react';
import Modal from '../../components/Modal';
import { tokenizeRuns, diffWordTokens, buildDiffTokens } from '../projectPage/chaptersContainer/diffUtils';

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'long', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Read-only word-level diff between the current chapter content and a
 * historical git snapshot.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   currentContent: string   — live chapter text (left / "current" side)
 *   snapshotContent: string | null  — null while loading
 *   commitInfo: { hash, shortHash, message, timestamp }
 *   snapshotLoading: boolean
 *   snapshotError: string | null
 */
export default class ChapterHistoryDiffModal extends Component {
  render() {
    const { isOpen, onClose, currentContent, snapshotContent, commitInfo, snapshotLoading, snapshotError } = this.props;
    if (!isOpen) return null;

    const label = commitInfo?.message?.split(' — ')[1]?.trim() || commitInfo?.message || '';
    const dateStr = commitInfo?.timestamp ? formatDate(commitInfo.timestamp) : '';

    let diffCount = 0;
    let snapshotRuns = [];
    let currentRuns = [];
    let ops = [];

    if (!snapshotLoading && !snapshotError && snapshotContent !== null) {
      const current = currentContent ?? '';
      const snapshot = snapshotContent ?? '';
      currentRuns = tokenizeRuns(current);
      snapshotRuns = tokenizeRuns(snapshot);
      const currentWords = currentRuns.filter((r) => r.type === 'word').map((r) => r.text);
      const snapshotWords = snapshotRuns.filter((r) => r.type === 'word').map((r) => r.text);
      ops = diffWordTokens(snapshotWords, currentWords);
      diffCount = ops.filter((o) => o.type !== 'eq').length;
    }

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="chars-modal-content">
          <div style={{ padding: '20px 24px 12px', borderBottom: '1px solid var(--border-1)' }}>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>{label || 'Snapshot'}</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-3)' }}>
              {dateStr}{commitInfo?.shortHash ? ` · ${commitInfo.shortHash}` : ''}
            </p>
          </div>

          <div className="chars-modal-body">
            {snapshotLoading && (
              <div className="summary-modal-loading">
                <div className="summary-modal-loading-spinner" />
                <div>Loading snapshot…</div>
              </div>
            )}

            {snapshotError && (
              <div className="summary-modal-error"><p>{snapshotError}</p></div>
            )}

            {!snapshotLoading && !snapshotError && snapshotContent !== null && (
              <>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-3)' }}>
                  {diffCount === 0
                    ? 'No differences — this snapshot matches your current chapter.'
                    : `${diffCount} word${diffCount === 1 ? '' : 's'} differ from your current chapter.`}
                </p>
                <div className="submissions-review__columns">
                  <section className="submissions-review__col">
                    <header className="submissions-review__col-head">Snapshot</header>
                    <div className="submissions-review__body submissions-review__body--diff">
                      {(() => {
                        const { tokens, trailingWs } = buildDiffTokens({ runs: snapshotRuns, ops, side: 'original' });
                        return (
                          <>
                            {tokens.map((t, i) => (
                              <React.Fragment key={i}>
                                {t.ws}
                                {t.highlight ? <span className="diff-removed">{t.word}</span> : <span>{t.word}</span>}
                              </React.Fragment>
                            ))}
                            <span>{trailingWs}</span>
                          </>
                        );
                      })()}
                    </div>
                  </section>
                  <section className="submissions-review__col">
                    <header className="submissions-review__col-head">Current</header>
                    <div className="submissions-review__body submissions-review__body--diff">
                      {(() => {
                        const { tokens, trailingWs } = buildDiffTokens({ runs: currentRuns, ops, side: 'submitted' });
                        return (
                          <>
                            {tokens.map((t, i) => (
                              <React.Fragment key={i}>
                                {t.ws}
                                {t.highlight ? <span className="diff-added">{t.word}</span> : <span>{t.word}</span>}
                              </React.Fragment>
                            ))}
                            <span>{trailingWs}</span>
                          </>
                        );
                      })()}
                    </div>
                  </section>
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>
    );
  }
}
